import { Link, useMatchRoute } from '@tanstack/react-router';
import { Home, Trophy, Target, BarChart3, Users, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCompany } from '@/lib/company-context';
import { BrandMark } from '@/components/shared/brand-mark';
import { UserMenu } from './user-menu';

const navItems = [
  { to: '/app', label: 'Home', icon: Home, minLevel: 1, exact: true },
  { to: '/app/leaderboard', label: 'Leaderboard', icon: Trophy, minLevel: 1 },
  { to: '/app/programs', label: 'Programs', icon: Target, minLevel: 1 },
  { to: '/app/dashboard', label: 'Dashboard', icon: BarChart3, minLevel: 5 },
  { to: '/app/team', label: 'Team', icon: Users, minLevel: 5 },
] as const;

export function Nav() {
  const { level } = useCompany();
  const matchRoute = useMatchRoute();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleItems = navItems.filter((item) => level >= item.minLevel);

  // Close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [matchRoute]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
      <div className="max-w-5xl mx-auto px-4 h-16 flex items-center gap-6">
        {/* Brand */}
        <Link to="/app" className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline text-sm font-bold text-foreground">WeeBee</span>
          <BrandMark className="h-6" alt="Go" />
        </Link>

        {/* Desktop Nav — text only, no icons */}
        <nav className="hidden md:flex items-center gap-1 flex-1">
          {visibleItems.map((item) => {
            const isActive = item.exact
              ? matchRoute({ to: item.to, fuzzy: false })
              : matchRoute({ to: item.to, fuzzy: true });
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 ml-auto">
          <UserMenu />

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav — keeps icons for scannability */}
      {mobileOpen && (
        <nav className="md:hidden border-t border-border bg-background px-4 py-2 animate-fade-in">
          {visibleItems.map((item) => {
            const isActive = item.exact
              ? matchRoute({ to: item.to, fuzzy: false })
              : matchRoute({ to: item.to, fuzzy: true });
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      )}
    </header>
  );
}
